import moment, { Moment } from "moment";

const DEFAULT_WORK_HISTORY_LOOKBACK_DAYS = 14;

/**
 * Computes the work-history date range shown in the 1:1 meeting sidebar.
 *
 * The range spans from the previous 1:1 (or 14 days ago when there is none) up
 * to the viewed meeting (clamped to "now" for future meetings). The bounds are
 * normalized so that `startDate <= endDate`: when the viewed meeting predates
 * its `previousMeeting` (e.g. an older meeting opened in a recurring series),
 * the raw bounds invert and the backend rejects the query with
 * "Start date must be before end date" (Sentry TOPICFLOW-JS-178).
 *
 * Profile pages with no upcoming 1:1 default to the most recent past meeting,
 * but their Work History should still show the recent rolling window.
 *
 * The `startDateIsInFuture` flag stays keyed on the raw start (the
 * previous-meeting date) so far-future meetings still surface the
 * "too far in the future" message rather than issuing a query.
 */
export function getWorkHistoryDateRange(args: {
  previousMeetingStartDate?: string | null;
  meetingStartDate?: string | null;
  useRollingDateRange?: boolean;
  now?: Moment;
}): { startDate: Moment; endDate: Moment; startDateIsInFuture: boolean } {
  const now = args.now ? args.now.clone() : moment();

  if (args.useRollingDateRange) {
    return {
      startDate: now
        .clone()
        .subtract(DEFAULT_WORK_HISTORY_LOOKBACK_DAYS, "days"),
      endDate: now.clone(),
      startDateIsInFuture: false,
    };
  }

  const startCandidate = args.previousMeetingStartDate
    ? moment(args.previousMeetingStartDate)
    : now.clone().subtract(DEFAULT_WORK_HISTORY_LOOKBACK_DAYS, "days");
  const meetingTimeOrNow = args.meetingStartDate
    ? moment(args.meetingStartDate)
    : now.clone();
  const endCandidate = meetingTimeOrNow.isAfter(now.clone().startOf("day"))
    ? now.clone()
    : meetingTimeOrNow;

  const startDateIsInFuture = startCandidate
    .clone()
    .startOf("day")
    .isAfter(now.clone().startOf("day"));

  const startDate = moment.min(startCandidate.clone(), endCandidate.clone());
  const endDate = moment.max(startCandidate.clone(), endCandidate.clone());

  return { startDate, endDate, startDateIsInFuture };
}
